import { useEffect, useMemo, useRef, useState } from 'react';
import { Ban, Bookmark, BookmarkPlus, Check, ChevronDown, Search, SlidersHorizontal, Trash2, Users, X } from 'lucide-react';
import {
  CARD_SORT_LABEL,
  configFromParams,
  configToParams,
  DENSITY_LABEL,
  LANE_LABEL,
  type LaneMode,
  DUE_FILTER_LABEL,
  DUE_FILTERS,
  PRIORITIES,
  filterChips,
  roleAtLeast,
  sameConfig,
  type BoardFilter,
  type BoardSnapshot,
  type BoardView,
  type CardSort,
  type Density,
  type SavedView,
  type ViewConfig,
} from '@kaba/shared';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { boardKey, queryClient } from '../../lib/board';
import { setQuery, useLocation } from '../../lib/router';
import { toast } from '../../lib/toast';
import {
  Avatar,
  Button,
  cx,
  DropdownCheckItem,
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSeparator,
  DropdownTrigger,
  LABEL_BG,
  Sheet,
} from '../ui/ui';

const DENSITY_KEY = 'kaba.density';

function storedDensity(): Density {
  try {
    const d = localStorage.getItem(DENSITY_KEY) as Density | null;
    if (d && d in DENSITY_LABEL) return d;
  } catch {
    /* privater Modus */
  }
  return 'standard';
}

/** Filter, Sortierung und Dichte stehen im URL (teilbar); die Dichte merkt sich zusätzlich das Gerät */
export function useViewConfig() {
  const { query } = useLocation();
  const qs = query.toString();
  const config = useMemo(() => configFromParams(new URLSearchParams(qs), { density: storedDensity() }), [qs]);
  const activeViewId = query.get('v');
  const update = (next: ViewConfig, opts: { viewId?: string | null } = {}) => {
    try {
      localStorage.setItem(DENSITY_KEY, next.density);
    } catch {
      /* egal */
    }
    const params: Record<string, string | null> = { ...configToParams(next) };
    if (next.view) params.view = next.view;
    if (opts.viewId !== undefined) params.v = opts.viewId;
    setQuery(params, { replace: true });
  };
  const setFilter = (f: BoardFilter) => update({ ...config, filter: f });
  return { config, update, setFilter, activeViewId };
}

const chip = (active: boolean) =>
  cx(
    'inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-sm whitespace-nowrap sm:min-h-9',
    active ? 'border-accent bg-accent-soft font-medium' : 'border-[var(--line)] hover:bg-black/5 dark:hover:bg-white/5',
  );

function toggle<T>(list: T[] | undefined, v: T): T[] | undefined {
  const next = list?.includes(v) ? list.filter((x) => x !== v) : [...(list ?? []), v];
  return next.length ? next : undefined;
}

export function FilterBar({ snapshot, view, onClose }: { snapshot: BoardSnapshot; view: BoardView; onClose: () => void }) {
  const { config, update, setFilter, activeViewId } = useViewConfig();
  const { data: me } = useMe();
  const f = config.filter;
  const set = (patch: Partial<BoardFilter>) => setFilter({ ...f, ...patch });

  // Suchtext lokal halten: die Adresse speichert ihn getrimmt – sonst ginge jedes Leerzeichen beim Tippen verloren
  const [q, setQ] = useState(f.q ?? '');
  const fqRef = useRef(f.q);
  fqRef.current = f.q;
  useEffect(() => {
    // Änderung von außen (Zurücksetzen, gespeicherte Ansicht, Zurück-Taste)
    setQ((cur) => ((cur.trim() || undefined) === (f.q || undefined) ? cur : (f.q ?? '')));
  }, [f.q]);
  useEffect(() => {
    const next = q.trim() || undefined;
    if (next === (fqRef.current || undefined)) return;
    const t = window.setTimeout(() => set({ q: next }), 150);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const whoLabel = () => {
    const n = f.assignees?.length ?? 0;
    if (!n) return 'Zuständig';
    if (n === 1) {
      const a = f.assignees![0]!;
      return a === 'me' ? 'Ich' : a === 'none' ? 'Niemand' : (snapshot.members.find((m) => m.id === a)?.name ?? 'Person');
    }
    return `Zuständig (${n})`;
  };

  return (
    <div className="flex flex-col gap-2 px-3 pb-2 sm:flex-row sm:items-center">
      <div className="relative min-w-0 sm:w-56 sm:flex-none">
        <Search size={16} className="absolute top-1/2 left-2.5 -translate-y-1/2 text-[var(--ink-soft)]" />
        <input
          autoFocus
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return;
            // Erst den Suchtext leeren, beim zweiten Escape die Leiste schließen (Filter bleiben)
            if (q) {
              setQ('');
              set({ q: undefined });
            } else onClose();
          }}
          placeholder="Karten durchsuchen …"
          aria-label="Karten durchsuchen"
          className="min-h-10 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] pr-2 pl-8 text-base sm:min-h-9 sm:text-sm"
        />
      </div>

      <div className="-mx-3 flex items-center gap-1.5 overflow-x-auto px-3 sm:mx-0 sm:px-0" role="toolbar" aria-label="Filter">
        {/* Zuständig */}
        <DropdownMenu>
          <DropdownTrigger className={chip(!!f.assignees?.length)}>
            <Users size={15} /> {whoLabel()} <ChevronDown size={14} />
          </DropdownTrigger>
          <DropdownContent align="start">
            <DropdownCheckItem checked={!!f.assignees?.includes('me')} onChange={() => set({ assignees: toggle(f.assignees, 'me') })}>
              Ich
            </DropdownCheckItem>
            <DropdownCheckItem checked={!!f.assignees?.includes('none')} onChange={() => set({ assignees: toggle(f.assignees, 'none') })}>
              Niemand zuständig
            </DropdownCheckItem>
            <DropdownSeparator />
            {snapshot.members
              .filter((m) => m.id !== me?.id)
              .map((m) => (
                <DropdownCheckItem key={m.id} checked={!!f.assignees?.includes(m.id)} onChange={() => set({ assignees: toggle(f.assignees, m.id) })}>
                  <Avatar user={m} size={20} /> {m.name}
                </DropdownCheckItem>
              ))}
          </DropdownContent>
        </DropdownMenu>

        {/* Labels */}
        {snapshot.labels.length > 0 && (
          <DropdownMenu>
            <DropdownTrigger className={chip(!!f.labels?.length)}>
              Labels{f.labels?.length ? ` (${f.labels.length})` : ''} <ChevronDown size={14} />
            </DropdownTrigger>
            <DropdownContent align="start">
              {snapshot.labels.map((l) => (
                <DropdownCheckItem key={l.id} checked={!!f.labels?.includes(l.id)} onChange={() => set({ labels: toggle(f.labels, l.id) })}>
                  <span className={cx('h-3 w-6 shrink-0 rounded-full', LABEL_BG[l.color])} aria-hidden />
                  {l.name || 'Ohne Namen'}
                </DropdownCheckItem>
              ))}
              <DropdownSeparator />
              <DropdownCheckItem checked={!!f.labels?.includes('none')} onChange={() => set({ labels: toggle(f.labels, 'none') })}>
                Ohne Label
              </DropdownCheckItem>
            </DropdownContent>
          </DropdownMenu>
        )}

        {/* Frist */}
        <DropdownMenu>
          <DropdownTrigger className={chip(!!f.due?.length)}>
            {f.due?.length === 1 ? DUE_FILTER_LABEL[f.due[0]!] : `Frist${f.due?.length ? ` (${f.due.length})` : ''}`} <ChevronDown size={14} />
          </DropdownTrigger>
          <DropdownContent align="start">
            {DUE_FILTERS.map((d) => (
              <DropdownCheckItem key={d} checked={!!f.due?.includes(d)} onChange={() => set({ due: toggle(f.due, d) })}>
                {DUE_FILTER_LABEL[d]}
              </DropdownCheckItem>
            ))}
          </DropdownContent>
        </DropdownMenu>

        {/* Priorität */}
        <DropdownMenu>
          <DropdownTrigger className={chip(!!f.priority?.length)}>
            Priorität{f.priority?.length ? ` (${f.priority.length})` : ''} <ChevronDown size={14} />
          </DropdownTrigger>
          <DropdownContent align="start">
            {[...PRIORITIES].reverse().map((p) => (
              <DropdownCheckItem key={p.value} checked={!!f.priority?.includes(p.value)} onChange={() => set({ priority: toggle(f.priority, p.value) })}>
                {p.label}
              </DropdownCheckItem>
            ))}
          </DropdownContent>
        </DropdownMenu>

        <button className={chip(!!f.blocked)} aria-pressed={!!f.blocked} onClick={() => set({ blocked: f.blocked ? undefined : true })}>
          <Ban size={15} /> Blockiert
        </button>

        <DisplayMenu config={config} view={view} onChange={(c) => update(c)} />
        <ViewsMenu snapshot={snapshot} config={{ ...config, view }} activeViewId={activeViewId} onApply={(v) => update(v.config, { viewId: v.id })} />

        <button
          aria-label="Filterleiste schließen (Filter bleiben aktiv)"
          title="Schließen – gesetzte Filter bleiben und stehen darunter"
          className="ml-auto inline-flex size-10 shrink-0 items-center justify-center rounded-lg text-[var(--ink-soft)] hover:bg-black/5 sm:size-9 dark:hover:bg-white/5"
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

/* ---------- Aktive Filter: immer sichtbar, einzeln entfernbar ---------- */

export function ActiveFilters({ snapshot, shownCount }: { snapshot: BoardSnapshot; shownCount: number }) {
  const { config, update } = useViewConfig();
  const chips = filterChips(config, {
    member: (id) => snapshot.members.find((m) => m.id === id)?.name,
    label: (id) => snapshot.labels.find((l) => l.id === id)?.name,
  });
  if (!chips.length) return null;
  const total = snapshot.cards.length;
  const narrowing = shownCount !== total;
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto px-3 pb-2" role="group" aria-label="Aktive Filter">
      <span className="shrink-0 text-xs whitespace-nowrap text-[var(--ink-soft)]" aria-live="polite">
        {narrowing ? `${shownCount} von ${total} Karten` : `${total} Karten`}
      </span>
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => update(c.without)}
          aria-label={`${c.label} – entfernen`}
          className="inline-flex min-h-9 shrink-0 items-center gap-1 rounded-full border border-accent/40 bg-accent-soft pr-1.5 pl-3 text-sm whitespace-nowrap hover:border-accent sm:min-h-8"
        >
          {c.label}
          <X size={14} aria-hidden className="text-[var(--ink-soft)]" />
        </button>
      ))}
      <button
        type="button"
        className="min-h-9 shrink-0 rounded-lg px-2 text-sm whitespace-nowrap text-accent-fg hover:underline sm:min-h-8"
        onClick={() => update({ ...config, filter: {}, sort: 'manual', lanes: undefined, emptyLanes: undefined }, { viewId: null })}
      >
        Alle entfernen
      </button>
    </div>
  );
}

/* ---------- Anzeige: Sortierung, Dichte, Erledigte ---------- */

export function DisplayMenu({ config, view, onChange }: { config: ViewConfig; view: BoardView; onChange: (c: ViewConfig) => void }) {
  const sortable = view === 'board' || view === 'list';
  const changed = config.sort !== 'manual' || config.density !== 'standard' || !!config.filter.completed || !!config.lanes;
  return (
    <DropdownMenu>
      <DropdownTrigger className={chip(changed)}>
        <SlidersHorizontal size={15} /> Anzeige <ChevronDown size={14} />
      </DropdownTrigger>
      <DropdownContent align="start">
        {sortable && (
          <>
            <DropdownLabel>Sortierung in Spalten</DropdownLabel>
            {(Object.keys(CARD_SORT_LABEL) as CardSort[]).map((s) => (
              <DropdownCheckItem key={s} checked={config.sort === s} keepOpen={false} onChange={() => onChange({ ...config, sort: s })}>
                {CARD_SORT_LABEL[s]}
              </DropdownCheckItem>
            ))}
            <DropdownSeparator />
          </>
        )}
        {(view === 'board' || view === 'list') && (
          <>
            <DropdownLabel>Kartengröße</DropdownLabel>
            {(Object.keys(DENSITY_LABEL) as Density[]).map((d) => (
              <DropdownCheckItem key={d} checked={config.density === d} keepOpen={false} onChange={() => onChange({ ...config, density: d })}>
                {DENSITY_LABEL[d]}
              </DropdownCheckItem>
            ))}
            <DropdownSeparator />
            <DropdownLabel>Zeilen (Swimlanes)</DropdownLabel>
            {([undefined, ...(Object.keys(LANE_LABEL) as LaneMode[])] as (LaneMode | undefined)[]).map((m) => (
              <DropdownCheckItem
                key={m ?? 'none'}
                checked={config.lanes === m}
                keepOpen={false}
                onChange={() => {
                  const next: ViewConfig = { ...config, lanes: m };
                  if (!m) delete next.emptyLanes;
                  onChange(next);
                }}
              >
                {m ? `nach ${LANE_LABEL[m]}` : 'Keine'}
              </DropdownCheckItem>
            ))}
            {config.lanes && (
              <DropdownCheckItem checked={!!config.emptyLanes} onChange={() => onChange({ ...config, emptyLanes: !config.emptyLanes })}>
                Leere Zeilen zeigen
              </DropdownCheckItem>
            )}
            <DropdownSeparator />
          </>
        )}
        <DropdownLabel>Erledigte Karten</DropdownLabel>
        {(
          [
            [undefined, 'Anzeigen'],
            ['hide', 'Ausblenden'],
            ['only', 'Nur erledigte'],
          ] as const
        ).map(([v, label]) => (
          <DropdownCheckItem
            key={label}
            checked={config.filter.completed === v}
            keepOpen={false}
            onChange={() => onChange({ ...config, filter: { ...config.filter, completed: v } })}
          >
            {label}
          </DropdownCheckItem>
        ))}
      </DropdownContent>
    </DropdownMenu>
  );
}

/* ---------- Gespeicherte Ansichten ---------- */

function ViewsMenu({
  snapshot,
  config,
  activeViewId,
  onApply,
}: {
  snapshot: BoardSnapshot;
  config: ViewConfig;
  activeViewId: string | null;
  onApply: (v: SavedView) => void;
}) {
  const { data: me } = useMe();
  const [dialog, setDialog] = useState(false);
  const canShare = roleAtLeast(snapshot.board.role, 'editor');
  const current = snapshot.views.find((v) => v.id === activeViewId) ?? snapshot.views.find((v) => sameConfig(v.config, config));
  const dirty = !!current && !sameConfig(current.config, config);
  const canWrite = (v: SavedView) => (v.shared ? canShare : v.ownerId === me?.id);
  const refresh = () => queryClient.invalidateQueries({ queryKey: boardKey(snapshot.board.id) });

  return (
    <>
      <DropdownMenu>
        <DropdownTrigger className={chip(!!current)}>
          <Bookmark size={15} /> {current ? current.name + (dirty ? ' *' : '') : 'Ansichten'} <ChevronDown size={14} />
        </DropdownTrigger>
        <DropdownContent align="start">
          {snapshot.views.length > 0 && <DropdownLabel>Gespeicherte Ansichten</DropdownLabel>}
          {snapshot.views.map((v) => (
            <DropdownItem key={v.id} icon={current?.id === v.id ? <Check size={16} className="text-accent-fg" /> : <Bookmark size={16} />} onSelect={() => onApply(v)}>
              <span className="flex-1">{v.name}</span>
              <span className="text-xs text-[var(--ink-soft)]">{v.shared ? 'Team' : 'Nur ich'}</span>
            </DropdownItem>
          ))}
          {snapshot.views.length > 0 && <DropdownSeparator />}
          {current && dirty && canWrite(current) && (
            <DropdownItem
              icon={<Check size={16} />}
              onSelect={async () => {
                try {
                  await api.patch(`/api/views/${current.id}`, { config });
                  await refresh();
                  toast.info(`„${current.name}“ aktualisiert.`);
                } catch (err) {
                  toast.error((err as Error).message);
                }
              }}
            >
              Änderungen in „{current.name}“ speichern
            </DropdownItem>
          )}
          <DropdownItem icon={<BookmarkPlus size={16} />} onSelect={() => setDialog(true)}>
            Aktuelle Ansicht speichern …
          </DropdownItem>
          {current && canWrite(current) && (
            <DropdownItem
              danger
              icon={<Trash2 size={16} />}
              onSelect={async () => {
                if (!window.confirm(`Ansicht „${current.name}“ löschen?`)) return;
                try {
                  await api.del(`/api/views/${current.id}`);
                  setQuery({ v: null }, { replace: true });
                  await refresh();
                } catch (err) {
                  toast.error((err as Error).message);
                }
              }}
            >
              „{current.name}“ löschen
            </DropdownItem>
          )}
        </DropdownContent>
      </DropdownMenu>
      <SaveViewDialog
        open={dialog}
        canShare={canShare}
        onClose={() => setDialog(false)}
        onSave={async (name, shared) => {
          const v = await api.post<SavedView>(`/api/boards/${snapshot.board.id}/views`, { name, shared, config });
          await refresh();
          setQuery({ v: v.id }, { replace: true });
          toast.info(`Ansicht „${name}“ gespeichert${shared ? ' – alle im Board sehen sie' : ''}.`);
        }}
      />
    </>
  );
}

function SaveViewDialog({
  open,
  canShare,
  onClose,
  onSave,
}: {
  open: boolean;
  canShare: boolean;
  onClose: () => void;
  onSave: (name: string, shared: boolean) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Ansicht speichern" side="center">
      <form
        className="flex flex-col gap-3 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          setBusy(true);
          try {
            await onSave(name.trim(), shared);
            setName('');
            setShared(false);
            onClose();
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="text-sm text-[var(--ink-soft)]">Speichert Filter, Sortierung, Kartengröße und Ansichtsart unter einem Namen.</p>
        <input
          autoFocus
          required
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z. B. Meine überfälligen"
          aria-label="Name der Ansicht"
          className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base sm:text-sm"
        />
        {canShare && (
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
            Für alle im Board sichtbar
          </label>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            Speichern
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
